import React, { useEffect, useMemo, useRef } from "react";
import Hls from "hls.js";
import { Box, Typography, Divider, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraTimeout from "./CameraTimeout";

type Props = {
  data: any;
  label: string,
  status: string;
  onClose?: () => void;
};

const FlespiVideoPlayer: React.FC<Props> = ({ data, label, status, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // ✅ converte string JSON em objeto apenas quando necessário
  const parsedData = useMemo(() => {
    if (!data) return null;

    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error("Erro ao fazer parse do JSON:", e);
        return null;
      }
    }

    return data;
  }, [data]);

  // Extrai informações do stream
  const stream = parsedData?.result?.[0] ?? null;
  const response = stream?.response ?? null;

  // A URL do stream HLS vem em response.url quando response.mediastream === "hls"
  const hlsUrl = response?.mediastream === "hls" && response?.url ? response.url : null;

  useEffect(() => {
    if (!hlsUrl || !videoRef.current) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(hlsUrl);
      hls.attachMedia(videoRef.current);

      return () => {
        hls.destroy();
      };
    } else {
      videoRef.current.src = hlsUrl;
    }
  }, [hlsUrl]);

  // Se não há dados ainda
  if (!parsedData?.result?.length) return (
    <Box
      className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-gtr-surface tw-text-gtr-text"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER */}
      <Box
        className="tw-bg-gtr-sidebar tw-text-white"
        sx={{
          px: 2,
          py: 1.5,
          backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
          color: "common.white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontFamily: "Prometo", fontWeight: 700 }}>Transmissão ao vivo</Typography>
          <Typography variant="caption" sx={{ opacity: 0.82 }}>{label}</Typography>
        </Box>
        {onClose && (
          <Tooltip title="Fechar" arrow>
            <IconButton aria-label="Fechar transmissão" onClick={onClose} sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider />

      {/* CONTENT */}
      <Box className="tw-min-h-[280px] tw-bg-slate-950" sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "#000"
      }}>
        <video
          ref={null}
          controls={false}
          autoPlay={false}
          style={{ width: "100%", height: "100%" }}

        />
      </Box>
      <Divider />

      {/* FOOTER */}
      <Box
        className="tw-bg-gtr-surface tw-text-gtr-text"
        sx={{
          p: 2,
          display: "flex",
          gap: 1,
          fontSize: 14,
        }}
      >
        {status === "timeout" && (
          <CameraTimeout
            onRetry={() => { }}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-gtr-surface tw-text-gtr-text"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* HEADER */}
      <Box
        className="tw-bg-gtr-sidebar tw-text-white"
        sx={{
          px: 2,
          py: 1.5,
          backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
          color: "common.white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
        <Typography variant="h6" sx={{ fontFamily: "Prometo", fontWeight: 700 }}>Transmissão ao vivo</Typography>
        <Typography variant="caption" sx={{ opacity: 0.82 }}>{label}</Typography>
        </Box>
        {onClose && (
          <Tooltip title="Fechar" arrow>
            <IconButton aria-label="Fechar transmissão" onClick={onClose} sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* VIDEO via HLS */}
      {status === "streaming" && hlsUrl && (
        <Box className="tw-min-h-[280px] tw-bg-slate-950" sx={{ bgcolor: "#000", flex: 1 }}>
          <video
            ref={videoRef}
            controls
            autoPlay
            style={{ width: "100%", height: "100%" }}
          />
        </Box>
      )}

      {/* Sem stream disponível */}
      {status === "streaming" && !hlsUrl && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#000",
            p: 3,
          }}
        >
          <Typography sx={{ color: "#fff", textAlign: "center" }}>
            Nenhum stream disponível para esta câmera.
          </Typography>
        </Box>
      )}

      <Divider />

      {/* FOOTER */}
      <Box
        className="tw-grid tw-grid-cols-2 tw-gap-2 tw-border-t tw-border-gtr-border/60 tw-bg-gtr-surface tw-p-3 sm:tw-grid-cols-4"
        sx={{
          p: 1.5,
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
          gap: 1,
          fontSize: 14,
        }}
      >
        {status === "streaming" && response && <>
          <Typography className="tw-rounded-lg tw-bg-gtr-surface-muted tw-px-3 tw-py-2" sx={{ fontSize: 13 }}>
            <b>Device:</b> {stream?.device_id ?? ""}
          </Typography>
          <Typography className="tw-rounded-lg tw-bg-gtr-surface-muted tw-px-3 tw-py-2" sx={{ fontSize: 13 }}>
            <b>Canal:</b> {response?.channel ?? ""}
          </Typography>
          <Typography className="tw-rounded-lg tw-bg-gtr-surface-muted tw-px-3 tw-py-2" sx={{ fontSize: 13 }}>
            <b>Resolução:</b> {response?.width ?? ""}x{response?.height ?? ""}
          </Typography>
          <Typography className="tw-rounded-lg tw-bg-gtr-surface-muted tw-px-3 tw-py-2" sx={{ fontSize: 13 }}>
            <b>Áudio:</b> {response?.has_audio ? "Sim" : "Não"}
          </Typography>
        </>}
        {status === "timeout" && (
          <CameraTimeout
            onRetry={() => { }}
          />
        )}
      </Box>
    </Box>
  );
};

export default FlespiVideoPlayer;
